import { Icon, IconButton, TableCell, TableRow } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Job, UserPermissions } from "generated/graphql";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import ScheduleJobPhase from "../ScheduleJobPhase/ScheduleJobPhase";
import { capitalCase } from "change-case";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import React from "react";
import dayjs from "dayjs";
import { groupPhasesByProductGroup } from "pages/jobs/JobsSchedulePage/components/groupPhases";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";

function CalendarButton({
  id,
  onSchedulesUpdate,
}: {
  id: Job["id"];
  onSchedulesUpdate: () => void;
}) {
  const { open, onOpen, onClose: modalClose } = useModalState();

  return (
    <>
      <IconButton onClick={() => onOpen()}>
        <Icon fontSize="medium">calendar_month</Icon>
      </IconButton>
      <Modal open={open} onClose={modalClose}>
        <ScheduleJobPhase id={id} onClose={modalClose} onScheduleSuccess={onSchedulesUpdate} />
      </Modal>
    </>
  );
}

export default function GroupedPhaseRows({
  jobPhases,
  onPhaseClick,
  onSchedulesUpdate,
}: {
  jobPhases: any[];
  onPhaseClick: (phaseId: string) => void;
  onSchedulesUpdate: () => void;
}) {
  const { hasPermissions } = useCheckPermissions();
  const items = groupPhasesByProductGroup(jobPhases);
  let rowIndex = 0;

  return (
    <>
      {items.map((item, idx) => {
        if (item.kind === "group") {
          return (
            <React.Fragment key={`group-${idx}`}>
              <TableRow sx={{ backgroundColor: "#f0f2f5" }}>
                <TableCell colSpan={8} sx={{ pl: 4 }}>
                  <MDTypography fontSize="13px" fontWeight="bold">
                    {item.groupName}
                  </MDTypography>
                </TableCell>
              </TableRow>
              {item.phases.map((jobPhase) => {
                const y = rowIndex++;
                return (
                  <PhaseTableRow
                    key={jobPhase.id}
                    jobPhase={jobPhase}
                    rowIndex={y}
                    indented
                    hasSchedulePermission={hasPermissions(UserPermissions.MANAGE_SCHEDULE)}
                    onPhaseClick={onPhaseClick}
                    onSchedulesUpdate={onSchedulesUpdate}
                  />
                );
              })}
            </React.Fragment>
          );
        }

        const y = rowIndex++;
        return (
          <PhaseTableRow
            key={item.phase.id}
            jobPhase={item.phase}
            rowIndex={y}
            hasSchedulePermission={hasPermissions(UserPermissions.MANAGE_SCHEDULE)}
            onPhaseClick={onPhaseClick}
            onSchedulesUpdate={onSchedulesUpdate}
          />
        );
      })}
    </>
  );
}

function PhaseTableRow({
  jobPhase,
  rowIndex,
  indented,
  hasSchedulePermission,
  onPhaseClick,
  onSchedulesUpdate,
}: {
  jobPhase: any;
  rowIndex: number;
  indented?: boolean;
  hasSchedulePermission: boolean;
  onPhaseClick: (phaseId: string) => void;
  onSchedulesUpdate: () => void;
}) {
  return (
    <TableRow
      className={rowIndex % 2 === 0 ? "inner even" : "inner odd"}
      sx={indented ? { backgroundColor: "#f7f7f7" } : undefined}
    >
      <TableCell align="center" sx={indented ? { pl: 6 } : undefined}>
        <MDBox sx={{ cursor: "pointer" }} onClick={() => onPhaseClick(jobPhase.id)}>
          {jobPhase.externalId}
        </MDBox>
      </TableCell>
      <TableCell sx={indented ? { pl: 4 } : undefined}>
        {jobPhase.organizationProductTypeName}
      </TableCell>
      <TableCell>{capitalCase(jobPhase.status)}</TableCell>
      <TableCell>
        {jobPhase.startsAt ? dayjs.utc(jobPhase.startsAt).local().format("LL") : ""}
      </TableCell>
      <TableCell>{jobPhase.jobDuration}</TableCell>
      <TableCell>{jobPhase.foremanUser?.name ?? "N/A"}</TableCell>
      <TableCell>{formatCentsToUSD(jobPhase.proposedOverallTotal)}</TableCell>
      <TableCell>
        {hasSchedulePermission && (
          <CalendarButton id={jobPhase.id} onSchedulesUpdate={onSchedulesUpdate} />
        )}
      </TableCell>
    </TableRow>
  );
}
